// Mind assessment — content const (M1).
// Source of truth: docs/MIND_CONTENT.md (49 items, verbatim) + docs/MIND_PLAN.md
// (model §2, scale §2, rename §3).
//
// A 7×7 MONOTONIC assessment: 7 subsystems × 7 parameters, each a first-person
// statement rated 1–5 on an agree scale where **higher is simply better**.
//
// ⚠️ This is NOT the Life Balance / Energetic Alignment model. Those are
// bidirectional "just-right" instruments (5 = ideal on 1–9; 4 = ideal on 1–7)
// where higher is *not* better. Mind takes its STRUCTURE from Energetic (7×7,
// `dimension`-grouped so a single subsystem can be taken on its own) but its
// SCORING from Shadow Work (`agree_disagree`) — more Distraction Resistance is
// simply more.
//
// ⚠️⚠️ **The 1–5 scale is NOT ours to choose.** `AssessmentQuestionRenderer`'s
// `renderAgreeDisagree` HARDCODES these five options and never reads
// `question.options` — so for this question type the seeded options are dead
// config. Shadow seeds `'[]'` and leans on the renderer; Personality seeds seven
// options that are silently ignored (its own copy says "five-point"). MIND_SCALE
// below therefore MIRRORS the renderer verbatim, labels included. Changing this
// scale means changing the renderer — which would move Personality and Shadow
// too, both shipped with live user data.
//
// ⚠️ No reverse-scored items. Every statement is written so that agreeing = more
// of the capacity; a reversed item would invert the radar and no scorer here
// handles them.

/** Fixed assessment row id (type 'mind'). …016 — …015 is the Archetype Matrix. */
export const MIND_ID = 'a1000000-0000-0000-0000-000000000016';

/**
 * The 1–5 agree scale — monotonic, higher = better.
 *
 * ⚠️ Copied VERBATIM (labels + casing) from `AssessmentQuestionRenderer`'s
 * `renderAgreeDisagree`, which is what the user actually sees. This const exists
 * so intro/legend copy can name the scale without re-deriving it — it does not
 * define it. A test asserts the two stay identical.
 */
export const MIND_SCALE: { value: number; label: string }[] = [
  { value: 1, label: 'Strongly Disagree' },
  { value: 2, label: 'Disagree' },
  { value: 3, label: 'Neutral' },
  { value: 4, label: 'Agree' },
  { value: 5, label: 'Strongly Agree' },
];

/** Label for a 1–7 rating (quiz selected-state + intro legend). */
export const mindScaleLabel = (value: number): string =>
  MIND_SCALE.find(s => s.value === value)?.label ?? '';

export interface MindParameter {
  /** Stable key — the scoring_key parameter. Never rename without a migration. */
  key: string;
  name: string;
  /** First-person statement, rated 1–7 against lived truth. */
  statement: string;
}

export interface MindSubsystem {
  id: string; // the question `dimension` — 'clarity' …
  order: number; // 1–7
  name: string;
  /** The spec's Primary Mode. Descriptive copy only — NOT a mechanic. */
  mode: string;
  /** The spec's game analogy — used in intro/insight copy. */
  analogy: string;
  essence: string;
  color: string;
  icon: string; // lucide name (data stays React-free, like the other src/data content)
  parameters: MindParameter[]; // 7
}

export const MIND_SUBSYSTEMS: MindSubsystem[] = [
  {
    id: 'clarity',
    order: 1,
    name: 'Clarity',
    mode: 'Regulate / Become Aware / Train',
    analogy: 'removing fog from the map',
    essence: 'understanding what is happening, what matters, and what to do next',
    color: '#0ea5e9',
    icon: 'Eye',
    parameters: [
      { key: 'mental_clarity', name: 'Mental clarity', statement: 'My thinking feels clear rather than foggy.' },
      { key: 'priority_clarity', name: 'Priority clarity', statement: 'I know what matters most right now.' },
      { key: 'goal_clarity', name: 'Goal clarity', statement: 'I can say plainly what I am working toward.' },
      { key: 'problem_clarity', name: 'Problem clarity', statement: 'When something is wrong, I can name the real problem rather than the symptom.' },
      { key: 'thought_organization', name: 'Thought organization', statement: 'I can take a tangle of thoughts and put them in an order that makes sense.' },
      { key: 'context_awareness', name: 'Context awareness', statement: 'I read situations accurately, including what is really going on around me.' },
      { key: 'next_action_clarity', name: 'Next-action clarity', statement: 'I know the next concrete step to take.' },
    ],
  },
  {
    id: 'focus',
    order: 2,
    name: 'Focus & Attention',
    mode: 'Train / Regulate / Track',
    analogy: 'aiming accuracy',
    essence: 'directing and sustaining attention',
    color: '#f59e0b',
    icon: 'Crosshair',
    parameters: [
      { key: 'attention_control', name: 'Attention control', statement: 'I choose where my attention goes rather than having it pulled.' },
      { key: 'deep_focus', name: 'Deep focus', statement: 'I can stay with work that demands my whole mind.' },
      { key: 'distraction_resistance', name: 'Distraction resistance', statement: 'I can let an interruption wait until I have finished.' },
      { key: 'task_switching_control', name: 'Task-switching control', statement: 'I stay with one task instead of scattering across several.' },
      { key: 'focus_duration', name: 'Focus duration', statement: 'I can hold my attention for as long as the work needs.' },
      { key: 'focus_recovery', name: 'Focus recovery', statement: 'After I am interrupted, I get back into the work quickly.' },
      { key: 'environmental_focus_support', name: 'Environmental focus support', statement: 'My surroundings help me concentrate rather than fight me.' },
    ],
  },
  {
    id: 'cognition',
    order: 3,
    name: 'Cognition',
    mode: 'Train / Track',
    analogy: 'the intelligence engine',
    essence: 'the raw thinking functions — memory, reasoning, processing, problem-solving',
    color: '#8b5cf6',
    icon: 'Cpu',
    parameters: [
      { key: 'memory', name: 'Memory', statement: 'I retain what I learn and can recall it when I need it.' },
      { key: 'reasoning', name: 'Reasoning', statement: 'I can follow an argument through and tell whether it holds.' },
      { key: 'problem_solving', name: 'Problem-solving', statement: 'I can break a hard problem apart and work it out.' },
      { key: 'processing_speed', name: 'Processing speed', statement: 'I understand new ideas quickly.' },
      // Spec said "Pattern recognition" — renamed: this is structure in the WORLD.
      // Metacognition's `loop_recognition` is the one about one's own head.
      { key: 'pattern_detection', name: 'Pattern Detection', statement: 'I notice repeating structures — in data, systems or events — before they are pointed out.' },
      { key: 'verbal_thinking', name: 'Verbal thinking', statement: 'I can think precisely in words and say what I actually mean.' },
      { key: 'visual_spatial_thinking', name: 'Visual / spatial thinking', statement: 'I can picture spaces, structures and diagrams in my head and work with them there.' },
    ],
  },
  {
    id: 'metacognition',
    order: 4,
    name: 'Metacognition',
    mode: 'Become Aware / Train',
    analogy: 'the player watching the character, not just being the character',
    essence: 'thinking about your thinking — observing yourself instead of being run by automatic thought',
    color: '#6366f1',
    icon: 'Glasses',
    parameters: [
      { key: 'self_observation', name: 'Self-observation', statement: 'I notice my own thoughts and reactions as they happen.' },
      { key: 'thought_labeling', name: 'Thought labeling', statement: 'I can tell what kind of thought I am having — a worry, a judgment, a plan, an assumption.' },
      // Spec said "Pattern recognition" — renamed: this is loops in ONESELF.
      { key: 'loop_recognition', name: 'Loop Recognition', statement: 'I recognise the mental loops I fall into, and could name the ones I repeat.' },
      { key: 'bias_awareness', name: 'Bias awareness', statement: 'I catch myself when my thinking is slanted or distorted.' },
      { key: 'trigger_awareness', name: 'Trigger awareness', statement: 'I know what sets my reactions off.' },
      { key: 'reflection_quality', name: 'Reflection quality', statement: 'When I look back on an experience, I actually take something from it.' },
      { key: 'self_correction', name: 'Self-correction', statement: 'When I see that I am wrong, I adjust — my thinking and my behaviour.' },
    ],
  },
  {
    id: 'emotional_regulation',
    order: 5,
    name: 'Emotional Regulation',
    mode: 'Regulate / Become Aware / Train',
    analogy: "the character's stability under pressure",
    essence: 'noticing, understanding and responding to emotion without being run by it — not suppression',
    color: '#10b981',
    icon: 'HeartPulse',
    parameters: [
      { key: 'emotional_awareness', name: 'Emotional awareness', statement: 'I notice what I am feeling while I am feeling it.' },
      { key: 'emotional_naming', name: 'Emotional naming', statement: 'I can name my emotions precisely, not just "good" or "bad".' },
      { key: 'emotional_tolerance', name: 'Emotional tolerance', statement: 'I can stay with a difficult feeling instead of rushing to escape it.' },
      { key: 'impulse_control', name: 'Impulse control', statement: 'When I am provoked, I do not act on the first impulse.' },
      { key: 'recovery_speed', name: 'Recovery speed', statement: 'After something knocks me off balance, I return to steady quickly.' },
      { key: 'stress_response', name: 'Stress response', statement: 'Under pressure I respond well, rather than shutting down or lashing out.' },
      { key: 'expression_quality', name: 'Expression quality', statement: 'I say what I feel clearly, to the right person, in a way they can hear.' },
    ],
  },
  {
    id: 'mindset',
    order: 6,
    name: 'Mindset & Narrative',
    mode: 'Become Aware / Train / Reframe',
    analogy: "the character's internal script",
    essence: 'the beliefs, assumptions and internal stories that shape behaviour',
    color: '#ec4899',
    icon: 'ScrollText',
    parameters: [
      { key: 'growth_mindset', name: 'Growth mindset', statement: 'I believe I can get better at the things I am currently bad at.' },
      { key: 'self_efficacy', name: 'Self-efficacy', statement: 'I believe my actions actually change outcomes.' },
      { key: 'self_talk', name: 'Self-talk', statement: 'The way I talk to myself is fair and useful.' },
      // Scored as AWARENESS of them, not absence — else it is non-monotonic and
      // the user doing the work scores lowest. See docs/MIND_CONTENT.md §6.
      { key: 'limiting_beliefs', name: 'Limiting beliefs', statement: 'I can name the beliefs that hold me back.' },
      { key: 'narrative_identity', name: 'Narrative identity', statement: 'The story I tell about my life is one I have chosen, not one I inherited.' },
      { key: 'resilience_frame', name: 'Resilience frame', statement: 'When things go badly, I can find a reading of it I can work with.' },
      { key: 'agency', name: 'Agency', statement: 'I feel like the author of my life rather than a passenger in it.' },
    ],
  },
  {
    id: 'decision_quality',
    order: 7,
    name: 'Decision Quality',
    mode: 'Train / Reflect / Execute',
    analogy: 'the command system — what action the character chooses next',
    essence: 'choosing actions, weighing trade-offs and following through',
    color: '#f97316',
    icon: 'GitBranch',
    parameters: [
      { key: 'problem_framing', name: 'Problem framing', statement: 'Before I decide, I make sure I am answering the real question.' },
      { key: 'option_generation', name: 'Option generation', statement: 'I look for more than the first two options.' },
      { key: 'consequence_awareness', name: 'Consequence awareness', statement: 'I think through what my choices will actually lead to.' },
      { key: 'value_alignment', name: 'Value alignment', statement: 'My decisions match what I actually care about.' },
      { key: 'risk_assessment', name: 'Risk assessment', statement: 'I am honest with myself about what could go wrong, and how likely it is.' },
      { key: 'commitment', name: 'Commitment', statement: 'Once I have decided, I commit instead of circling.' },
      { key: 'review', name: 'Review', statement: 'I go back and learn from decisions I have already made.' },
    ],
  },
];

export const MIND_SUBSYSTEM_IDS = MIND_SUBSYSTEMS.map(s => s.id);

export function getMindSubsystem(id: string): MindSubsystem | undefined {
  return MIND_SUBSYSTEMS.find(s => s.id === id);
}

/** Every item, flattened in seed order (subsystem 1..7 × parameter 1..7). */
export const MIND_ITEMS: { subsystem: MindSubsystem; parameter: MindParameter; orderIndex: number }[] =
  MIND_SUBSYSTEMS.flatMap((subsystem, si) =>
    subsystem.parameters.map((parameter, pi) => ({ subsystem, parameter, orderIndex: si * 7 + pi + 1 }))
  );
